import { StepItem, AlertBox, Screenshot, ScreenshotGrid, BulletList } from '../components/UIComponents';
import { Camera, FileText, Clock, RadioTower, Upload, ShieldAlert, Info } from 'lucide-react';

export function CredentialsConfigurationSection() {
    return (
        <div className="space-y-8 mt-6">
            {/* ── Camera Credentials ── */}
            <section className="doc-section scroll-mt-20" id="camera-creds">
                <div className="mb-3 pb-2 border-b border-white/10">
                    <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                        <Camera className="text-red-500" size={22} />
                        Camera Credential Configuration
                    </h2>
                    <p className="text-[14px] text-gray-200 mt-1.5 leading-relaxed">
                        Camera credentials are used to scan and monitor IP cameras and surveillance devices.
                    </p>
                </div>
                <div className="flex flex-col gap-4 group">
                    <StepItem num={1} title="Open Credential Configuration">
                        Login to the portal → Navigate to <strong className="text-gray-200">Agents → Credentials Config</strong> → Click <strong className="text-gray-200">Add Credential</strong> → Select <strong className="text-gray-200">Add Configuration</strong>.
                        <Screenshot src="/Open_Credential_Configuration.png" alt="Open Credential Configuration" />
                    </StepItem>
                    <StepItem num={2} title="Configure Camera Credentials">
                        <BulletList items={[
                            'Enter the Configuration Name',
                            <span>Select Credential Type as <strong className="text-gray-200">Camera</strong></span>,
                            'Select the target camera asset(s)',
                            <span>Enter Username, Password, and Make (Optional)</span>,
                            'Save the configuration and initiate the scan'
                        ]} />
                        <Screenshot src="/Configure_Camera_Credentials.png" alt="Configure Camera Credentials" />
                    </StepItem>
                    <StepItem num={3} title="Verify Uploaded Camera Assets">
                        Navigate to <strong className="text-gray-200">Assets</strong> → Click <strong className="text-gray-200">Cameras</strong> from the top menu. View Camera Status, IP, Device Info, and Connectivity.
                        <Screenshot src="/Verify_Uploaded_Camera_Assets.png" alt="Verify Uploaded Camera Assets" />
                    </StepItem>
                    <StepItem num={4} title="Verify Camera Details">
                        Click on a specific camera asset to view its detailed categories.
                        <Screenshot src="/Verify_Camera_Details.png" alt="Verify Camera Details" />
                        <div className="bg-gradient-to-br from-gray-800 to-gray-800/50 border border-white/5 rounded-xl p-5 mt-5">
                            <div className="text-[14px] font-bold text-gray-100 mb-2 flex items-center gap-2">
                                <Camera size={16} className="text-red-400" />
                                Camera Detail Page Categories
                            </div>
                            <BulletList items={[
                                'Open Ports & Banner Information',
                                'ARP Details & WebSocket Information',
                                'Running Services & RTSP Details',
                                'HTTP & Network Information',
                                'Device Metadata'
                            ]} />
                        </div>
                    </StepItem>
                    <StepItem num={5} title="Camera Frame Capture">
                        <AlertBox type="note" icon={<Info size={16} />}>
                            The agent automatically captures the latest camera frame every <strong className="text-blue-300">10 minutes</strong> and updates it in the portal, helping monitor camera activity and verify availability.
                        </AlertBox>
                        <ScreenshotGrid>
                            <Screenshot src="/Camera_Frame_Capture_Update.png" alt="Camera Frame Capture Update" />
                            <Screenshot src="/Camera_Frame_Capture_Update1.png" alt="Camera Frame Capture Details" />
                        </ScreenshotGrid>
                    </StepItem>
                </div>
            </section>

            {/* ── Camera Template ── */}
            <section className="doc-section scroll-mt-20" id="camera-template">
                <div className="mb-3 pb-2 border-b border-white/10">
                    <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                        <FileText className="text-yellow-500" size={22} />
                        Camera Credential Template
                    </h2>
                    <p className="text-[14px] text-gray-200 mt-1.5 leading-relaxed">
                        Use the Excel template to bulk-upload camera credentials.
                    </p>
                </div>
                <div className="flex flex-col gap-4 group">
                    <StepItem num={1} title="Download the Template">
                        Go to <strong className="text-gray-200">Assets</strong> page → Click <strong className="text-gray-200">Import Template</strong> → Download the credential template.
                        <Screenshot src="/Download_the_Template.png" alt="Download the Template" />
                    </StepItem>
                    <StepItem num={2} title="Update the Template">
                        Open the Excel template and fill in: Asset Name, IP Address, Asset Type (Camera), Username, Password, and Make.
                        <Screenshot src="/Update_the_Template_Reference_Template.png" alt="Update the Template" />
                    </StepItem>
                    <StepItem num={3} title="Upload Credentials Using Excel">
                        Return to the portal → Click <strong className="text-gray-200">Import</strong> → Select the file → Select the Agent → Click <strong className="text-gray-200">Upload</strong>.
                        <Screenshot src="/Upload Credentials Using Excel.png" alt="Upload Credentials Using Excel" />
                    </StepItem>
                    <StepItem num={4} title="Upload Confirmation">
                        A toast notification will confirm: <em className="text-green-400">"Credentials uploaded successfully."</em>
                        <Screenshot src="/Upload_Confirmation.png" alt="Upload Confirmation" />
                    </StepItem>
                    <StepItem num={5} title="Verify Uploaded Assets">
                        Navigate to the <strong className="text-gray-200">Assets</strong> page to see all uploaded camera assets.
                        <Screenshot src="/Verify_Uploaded_Assets.png" alt="Verify Uploaded Assets" />
                    </StepItem>
                    <StepItem num={6} title="Verify Credentials Under Agent">
                        <strong className="text-gray-200">Agents</strong> → Select Agent → <strong className="text-gray-200">Actions</strong> → <strong className="text-gray-200">Credential Config</strong> to view uploaded credentials.
                        <ScreenshotGrid>
                            <Screenshot src="/Verify_Credentials_Under_Agent.png" alt="Verify Credentials Under Agent" />
                            <Screenshot src="/Verify_Credentials_Under_Agent1.png" alt="Verify Credentials Details" />
                        </ScreenshotGrid>
                    </StepItem>
                    <StepItem num={7} title="Edit or Delete Credentials">
                        You can edit existing credentials or delete them as needed.
                        <Screenshot src="/Edit_or_Delete_Credentials.png" alt="Edit or Delete Credentials" />
                    </StepItem>
                </div>
            </section>

            {/* ── Camera Scheduler ── */}
            <section className="doc-section scroll-mt-20" id="camera-scheduler">
                <div className="mb-3 pb-2 border-b border-white/10">
                    <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                        <Clock className="text-teal-500" size={22} />
                        Camera Scan Scheduler
                    </h2>
                </div>
                <div className="flex flex-col gap-4 group">
                    <StepItem num={1} title="Open Scheduler Settings">
                        Login → <strong className="text-gray-200">Settings</strong> → <strong className="text-gray-200">Scheduler</strong>.
                        <Screenshot src="/Open_Scheduler_Settings.png" alt="Open Scheduler Settings" />
                    </StepItem>
                    <StepItem num={2} title="Add New Scheduler">
                        Click <strong className="text-gray-200">Add Scheduler</strong> and enter the Scheduler Name.
                    </StepItem>
                    <StepItem num={3} title="Select Task Type">
                        In Task Type, select <strong className="text-gray-200">Camera Scan</strong>.
                    </StepItem>
                    <StepItem num={4} title="Enable Scheduler">
                        Toggle the scheduler <strong className="text-gray-200">ON</strong> to allow automatic triggering.
                    </StepItem>
                    <StepItem num={5} title="Select Agent">
                        Select the agent for which the camera scan should run.
                    </StepItem>
                    <StepItem num={6} title="Configure Timing">
                        Set the scan interval:
                        <BulletList items={[
                            'Daily / Hourly / Weekly / Custom Interval',
                            'Recommended timezone for India: Asia/Kolkata'
                        ]} />
                    </StepItem>
                    <StepItem num={7} title="Save Scheduler">
                        Click <strong className="text-gray-200">Save Scheduler</strong> to activate.
                    </StepItem>
                </div>
            </section>

            {/* ── SNMP Credentials ── */}
            <section className="doc-section scroll-mt-20" id="snmp-creds">
                <div className="mb-3 pb-2 border-b border-white/10">
                    <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                        <RadioTower className="text-orange-500" size={22} />
                        SNMP Credential Configuration
                    </h2>
                    <p className="text-[14px] text-gray-200 mt-1.5 leading-relaxed">
                        SNMP credentials are used to communicate with network devices such as switches, routers, firewalls, cameras, and other SNMP-enabled assets.
                    </p>
                </div>
                <AlertBox type="note" icon={<Info size={16} />}>
                    <strong className="text-blue-300">Prerequisites:</strong> SNMP must be enabled on the target device. Required SNMP versions: v1, v2, or v3.
                    The target asset must already be added in the Ovul Scanner portal.
                </AlertBox>
                <Screenshot src="/SNMP_Credential_Configuration_Prerequisites.png" alt="SNMP Credential Configuration Prerequisites" />
                <div className="flex flex-col gap-4 group mt-5">
                    <StepItem num={1} title="Open Credentials Configuration">
                        Login → <strong className="text-gray-200">Agents</strong> → <strong className="text-gray-200">Credentials Config</strong>.
                        <Screenshot src="/Open_Credentials_Configuration.png" alt="Open Credentials Configuration" />
                    </StepItem>
                    <StepItem num={2} title="Add New Credential">
                        Click <strong className="text-gray-200">Add Credential</strong> → <strong className="text-gray-200">Add Configuration</strong>.
                    </StepItem>
                    <StepItem num={3} title="Configure SNMP Credentials">
                        <BulletList items={[
                            'Enter SNMP Configuration Name',
                            <span>Select Credential Type as <strong className="text-gray-200">SNMP</strong></span>,
                            'Select target asset(s)',
                            'Choose SNMP Version: v1, v2, or v3',
                            'Enter required credential details',
                            <span>Click <strong className="text-gray-200">Save Config</strong></span>
                        ]} />
                        <Screenshot src="/Configure_SNMP_Credentials.png" alt="Configure SNMP Credentials" />
                    </StepItem>
                </div>
                <div className="bg-gradient-to-br from-gray-800 to-gray-800/50 border border-white/5 rounded-xl p-5 mt-5">
                    <div className="text-[14px] font-bold text-gray-100 mb-2 flex items-center gap-2">
                        <Clock size={16} className="text-orange-400" />
                        SNMP Scan Scheduling
                    </div>
                    <BulletList items={[
                        <span>By default, scans run every <strong className="text-gray-200">6 hours</strong></span>,
                        'Manual scans can be initiated anytime',
                        'Scheduled scans can be configured via the Scheduler'
                    ]} />
                    <Screenshot src="/SNMP_Scan_Scheduling.png" alt="SNMP Scan Scheduling" />
                </div>
            </section>

            {/* ── SNMP Bulk Upload ── */}
            <section className="doc-section scroll-mt-20" id="snmp-bulk">
                <div className="mb-3 pb-2 border-b border-white/10">
                    <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                        <Upload className="text-blue-500" size={22} />
                        SNMP Bulk Upload via Template
                    </h2>
                </div>
                <div className="flex flex-col gap-4 group">
                    <StepItem num={1} title="Download the SNMP Template">
                        Login → <strong className="text-gray-200">Agents</strong> → <strong className="text-gray-200">Import Template</strong> → Download the SNMP credential template.
                        <Screenshot src="/Download_the_Template_for_SNMP_Credential_Template.png" alt="Download the SNMP Template" />
                    </StepItem>
                    <StepItem num={2} title="Update the Template">
                        Fill in the required SNMP fields in the Excel template.
                        <Screenshot src="/Update_the_Template.png" alt="Update the Template" />
                    </StepItem>
                    <StepItem num={3} title="Upload the Template">
                        Return to the portal → <strong className="text-gray-200">Import</strong> → Select file → Select Agent → <strong className="text-gray-200">Upload</strong>.
                    </StepItem>
                    <StepItem num={4} title="Verify Upload Status">
                        A success notification will confirm the upload.
                        <Screenshot src="/Verify_Upload_Status.png" alt="Verify Upload Status" />
                    </StepItem>
                    <StepItem num={5} title="Verify SNMP Assets">
                        Navigate to <strong className="text-gray-200">Assets</strong> and verify all SNMP-enabled assets are listed.
                        <Screenshot src="/Verify_Uploaded_SNMP_Assets.png" alt="Verify SNMP Assets" />
                    </StepItem>
                    <StepItem num={6} title="Verify Credentials Under Agent">
                        <strong className="text-gray-200">Agents</strong> → Select Agent → <strong className="text-gray-200">Actions</strong> → <strong className="text-gray-200">Credential Config</strong>.
                        <ScreenshotGrid>
                            <Screenshot src="/Verify_Credentials_Under_Agent_Bulk_Upload_of_SNMP_Credentials.png" alt="Verify Bulk Credentials Under Agent" />
                            <Screenshot src="/Verify_Credentials_Under_Agent_Bulk_Upload_of_SNMP_Credentials1.png" alt="Verify Bulk Credentials Details" />
                        </ScreenshotGrid>
                    </StepItem>
                    <StepItem num={7} title="Automatic Scan Initiation">
                        The agent automatically receives and initiates SNMP scans based on the configured schedule.
                        <ScreenshotGrid>
                            <Screenshot src="/Automatic_Scan_Initiation.png" alt="Automatic Scan Initiation" />
                            <Screenshot src="/Automatic_Scan_Initiation1.png" alt="Automatic Scan Status" />
                        </ScreenshotGrid>
                    </StepItem>
                </div>

                <div className="bg-gradient-to-b from-gray-900/80 to-gray-900/30 border border-white/5 shadow-xl shadow-black/20 rounded-xl p-5 mt-5">
                    <div className="text-[14px] font-bold text-gray-100 mb-4 flex items-center gap-2">
                        <FileText size={16} className="text-blue-400" />
                        Required SNMP Template Fields
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
                        {[
                            { field: 'Asset Name', req: 'All versions', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
                            { field: 'IP Address', req: 'All versions', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
                            { field: 'SNMP Version', req: 'All versions', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
                            { field: 'Community String', req: 'SNMP v1 / v2c', color: 'bg-green-500/10 text-green-400 border-green-500/20' },
                            { field: 'SNMP Username', req: 'SNMP v3', color: 'bg-orange-500/10 text-orange-400 border-orange-500/20' },
                            { field: 'Auth Protocol', req: 'SNMP v3', color: 'bg-orange-500/10 text-orange-400 border-orange-500/20' },
                            { field: 'Auth Password', req: 'SNMP v3', color: 'bg-orange-500/10 text-orange-400 border-orange-500/20' },
                            { field: 'Privacy Protocol', req: 'SNMP v3', color: 'bg-orange-500/10 text-orange-400 border-orange-500/20' },
                            { field: 'Privacy Password', req: 'SNMP v3', color: 'bg-orange-500/10 text-orange-400 border-orange-500/20' }
                        ].map(({ field, req, color }) => (
                            <div key={field} className="flex justify-between items-center p-3 rounded-lg bg-black/20 border border-white/5 hover:border-orange-500/20 hover:bg-black/40 transition-all duration-300 group">
                                <span className="text-[12.5px] text-gray-300 font-medium group-hover:text-gray-100 transition-colors">{field}</span>
                                <span className={`text-[10px] font-bold tracking-wide px-2 py-0.5 rounded border ${color}`}>
                                    {req}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Firewall Credentials ── */}
            <section className="doc-section scroll-mt-20" id="firewall-creds">
                <div className="mb-3 pb-2 border-b border-white/10">
                    <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                        <ShieldAlert className="text-rose-500" size={22} />
                        Firewall Credential Configuration
                    </h2>
                    <p className="text-[14px] text-gray-200 mt-1.5 leading-relaxed">
                        Firewall credentials are used to scan and monitor firewall devices within the environment.
                    </p>
                </div>
                <div className="flex flex-col gap-4 group">
                    <StepItem num={1} title="Open Credential Configuration">
                        Login → <strong className="text-gray-200">Agents → Credentials Config</strong> → <strong className="text-gray-200">Add Credential</strong> → <strong className="text-gray-200">Add Configuration</strong>.
                        <Screenshot src="/Open_Credential_Configuration.png" alt="Open Credential Configuration" />
                    </StepItem>
                    <StepItem num={2} title="Configure Firewall Credentials">
                        <BulletList items={[
                            'Enter the Firewall Configuration Name',
                            <span>Select Credential Type as <strong className="text-gray-200">Firewall</strong></span>,
                            'Select the target firewall asset(s)',
                            'Enter the required firewall credentials',
                            'Save the configuration',
                            'Start the scan'
                        ]} />
                        <Screenshot src="/Configure Firewall Credentials.png" alt="Configure Firewall Credentials" />
                    </StepItem>
                </div>
            </section>
        </div>
    );
}
